import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { publicCmsAPI } from '../../lib/cmsApi';
import {
  Droplets,
  Building2,
  Route,
  Trees,
  FileText,
  FolderKanban,
  ArrowRight,
  CheckCircle,
  Shield,
  Wrench,
  Lightbulb,
  GraduationCap,
  Target,
  BookOpen,
  Loader2,
} from 'lucide-react';

// Icon mapping
const iconMap = {
  Droplets,
  Building2,
  Route,
  Trees,
  FileText,
  FolderKanban,
  Shield,
  Wrench,
  Lightbulb,
  GraduationCap,
  Target,
  BookOpen,
};

const ServicesPage = () => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [formations, setFormations] = useState([]);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, formationsRes, pageRes] = await Promise.all([
          publicCmsAPI.getServices(),
          publicCmsAPI.getFormations(),
          publicCmsAPI.getPage('services').catch(() => ({ data: null })),
        ]);
        
        setServices(servicesRes.data || []);
        setFormations(formationsRes.data || []);
        setPageContent(pageRes.data);
      } catch (error) {
        console.error('Error fetching services data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const getSection = (key) => {
    if (!pageContent?.sections) return null;
    return pageContent.sections.find(s => s.section_key === key);
  };

  const heroSection = getSection('hero');
  const focusSection = getSection('focus_ruissellement');
  const formationsIntro = getSection('formations_intro');

  const publicCible = formationsIntro?.custom_data?.public_cible || [
    'Ingénieurs civils et hydrauliques',
    'Architectes et urbanistes',
    'Gestionnaires municipaux',
    'Professionnels impliqués dans des projets d\'infrastructures publiques et privées',
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-[#1D1D1B] py-20 lg:py-24">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="text-[#FFD537] font-semibold mb-4 animate-fadeIn">
              {heroSection?.eyebrow || 'EXPERTISE PLURIDISCIPLINAIRE'}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6 animate-fadeIn">
              {heroSection?.title || 'Une expertise spécialisée au service des infrastructures urbaines'}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed animate-fadeIn animation-delay-100">
              {heroSection?.content || 'PEY Consultants met à profit une expertise technique pointue en ingénierie municipale, avec une spécialisation reconnue en hydrologie urbaine, modélisation hydraulique et gestion intégrée de l\'eau.'}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section 
        className="py-12"
        style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
      >
        <div className="container-app">
          <p className="text-center text-[#1D1D1B] text-lg font-medium max-w-3xl mx-auto">
            Notre approche combine analyse scientifique, planification stratégique et conception durable afin d'optimiser la performance des infrastructures et de renforcer la résilience des collectivités.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="space-y-20">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Droplets;
              return (
                <div 
                  key={service.id}
                  className={`grid lg:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                      >
                        <IconComponent className="w-8 h-8 text-[#F9B438]" />
                      </div>
                      <div>
                        <span className="text-[#F9B438] font-bold text-sm">{String(index + 1).padStart(2, '0')}.</span>
                        <h2 className="font-heading text-2xl font-bold text-[#1D1D1B]">{service.title}</h2>
                      </div>
                    </div>
                    
                    <p className="text-neutral-600 leading-relaxed mb-6">
                      {service.full_description || service.short_description}
                    </p>

                    {service.bullet_points?.length > 0 && (
                      <ul className="space-y-3 mb-6">
                        {service.bullet_points.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div 
                              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)' }}
                            >
                              <CheckCircle className="w-4 h-4 text-[#F9B438]" />
                            </div>
                            <span className="text-neutral-700">{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {service.objective && (
                      <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-[#F9B438]">
                        <p className="text-neutral-700">
                          <strong>Notre objectif :</strong> {service.objective}
                        </p>
                      </div>
                    )}

                    {service.approaches?.length > 0 && (
                      <div className="space-y-3 mt-6">
                        <p className="font-semibold text-[#1D1D1B] mb-3">Nos cadres d'intervention</p>
                        {service.approaches.map((approach, idx) => (
                          <div key={idx} className="p-4 bg-neutral-50 rounded-lg">
                            <p className="font-semibold text-[#1D1D1B] mb-1">{approach.title}</p>
                            <p className="text-neutral-600 text-sm">{approach.description}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <Card className="overflow-hidden">
                      <div className="aspect-[4/3] bg-neutral-100 flex items-center justify-center">
                        {service.image_url ? (
                          <img 
                            src={service.image_url} 
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <IconComponent className="w-24 h-24 text-neutral-200" />
                        )}
                      </div>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Focus Section - Gestion des eaux de ruissellement */}
      <section className="section-padding bg-neutral-50">
        <div className="container-app">
          <div className="text-center mb-12">
            <p className="text-[#F9B438] font-semibold mb-2">{focusSection?.eyebrow || 'FOCUS'}</p>
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">
              {focusSection?.title || 'Gestion des eaux de ruissellement'}
            </h2>
            <p className="text-neutral-600 max-w-3xl mx-auto">
              {focusSection?.content || 'La gestion des eaux de ruissellement constitue un pilier central de notre expertise.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {(focusSection?.items || [
              {
                icon: 'Shield',
                title: 'Approche préventive',
                subtitle: 'Anticiper pour mieux protéger.',
                bullets: [
                  'Planification stratégique à l\'échelle des bassins versants',
                  'Diagnostics hydrologiques et hydrauliques',
                  'Modélisation des scénarios de pluie de conception',
                  'Plans d\'action et stratégies de mitigation',
                ],
              },
              {
                icon: 'Wrench',
                title: 'Approche curative',
                subtitle: 'Intervenir avec rigueur après les sinistres.',
                bullets: [
                  'Rapports techniques post-événement',
                  'Analyse des zones sinistrées et des défaillances',
                  'Évaluation des capacités résiduelles',
                  'Recommandations correctives durables',
                ],
              },
              {
                icon: 'Lightbulb',
                title: 'Approche innovante',
                subtitle: 'Innover pour mieux s\'adapter.',
                bullets: [
                  'Recherche appliquée et développement méthodologique',
                  'Collaboration académique et technique',
                  'Formation des gestionnaires et professionnels',
                  'Intégration de solutions fondées sur la nature',
                ],
              },
            ]).map((item, index) => {
              const IconComponent = iconMap[item.icon] || Shield;
              return (
                <Card key={index}>
                  <CardContent className="p-6">
                    <IconComponent className="w-10 h-10 text-[#F9B438] mb-4" />
                    <h3 className="font-heading font-semibold text-lg mb-2 text-[#1D1D1B]">{item.title}</h3>
                    <p className="text-neutral-600 text-sm font-medium mb-2">{item.subtitle}</p>
                    <ul className="text-neutral-600 text-sm space-y-1">
                      {item.bullets?.map((bullet, idx) => (
                        <li key={idx}>• {bullet}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="p-8 bg-white rounded-xl shadow-lg">
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
              >
                <Target className="w-6 h-6 text-[#1D1D1B]" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl mb-2 text-[#1D1D1B]">Notre objectif</h3>
                <p className="text-neutral-600">
                  {focusSection?.custom_data?.objective || 'Accompagner nos clients vers une gestion intégrée, structurée et résiliente des ressources en eau.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formations Section */}
      {formations.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-app">
            <div className="text-center mb-12">
              <p className="text-[#F9B438] font-semibold mb-2">FORMATIONS</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">
                {formationsIntro?.title || 'Développer les compétences en gestion durable des eaux'}
              </h2>
              <p className="text-neutral-600 max-w-3xl mx-auto">
                {formationsIntro?.content || 'PEY Consultants contribue activement au transfert de connaissances et au perfectionnement des professionnels.'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {formations.map((formation, index) => (
                <Card key={formation.id} className="h-full">
                  <CardContent className="p-6">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                    >
                      <GraduationCap className="w-6 h-6 text-[#F9B438]" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2 text-[#1D1D1B]">{formation.title}</h3>
                    <p className="text-neutral-600 text-sm mb-3">{formation.short_intro}</p>
                    {formation.learning_outcomes?.length > 0 && (
                      <ul className="text-neutral-600 text-sm space-y-1">
                        {formation.learning_outcomes.slice(0, 4).map((outcome, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-[#F9B438]">•</span>
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {formation.external_link && (
                      <a 
                        href={formation.external_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-4 text-sm text-[#F9B438] hover:underline"
                      >
                        En savoir plus →
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Public cible */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <Card className="p-8">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                >
                  <BookOpen className="w-8 h-8 text-[#F9B438]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1D1D1B] mb-4">Public cible</h3>
                <p className="text-neutral-600 mb-4">Nos formations s'adressent aux :</p>
                <ul className="space-y-2">
                  {publicCible.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                      />
                      <span className="text-neutral-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <div>
                <h3 className="font-heading text-xl font-semibold text-[#1D1D1B] mb-4">Notre engagement en formation</h3>
                <p className="text-neutral-600 leading-relaxed mb-6">
                  Nous croyons que le développement des compétences est essentiel pour assurer une gestion durable, rigoureuse et conforme des infrastructures hydrauliques.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#1D1D1B] py-16">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Besoin d'expertise en ingénierie?
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contactez notre équipe pour discuter de vos besoins spécifiques et obtenir une soumission personnalisée.
          </p>
          <Link to="/contact">
            <Button size="lg">
              Demander une soumission
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
